/**
 * Common toast notification patterns
 *
 * Pre-configured toast messages for common scenarios throughout the application.
 * This ensures consistency in messaging and reduces code duplication.
 */

import { toast } from "../hooks/useToast";

/**
 * Authentication-related toasts
 */
export const authToasts = {
  loginSuccess: () => {
    toast.success("Welcome back!", {
      description: "You have successfully logged in",
    });
  },

  loginError: (message?: string) => {
    toast.error("Login failed", {
      description: message || "Please check your credentials and try again",
    });
  },

  signupSuccess: () => {
    toast.success("Account created successfully!", {
      description: "Welcome to Auqli Store",
    });
  },

  signupError: (message?: string) => {
    toast.error("Failed to create account", {
      description: message || "Please check your information and try again",
    });
  },

  logoutSuccess: () => {
    toast.success("Logged out successfully", {
      description: "See you again soon!",
    });
  },

  sessionExpired: () => {
    toast.warning("Session expired", {
      description: "Please log in again to continue",
    });
  },

  passwordResetSent: () => {
    toast.success("Password reset email sent", {
      description: "Check your inbox for instructions",
    });
  },

  passwordResetSuccess: () => {
    toast.success("Password updated successfully", {
      description: "You can now log in with your new password",
    });
  },

  interestsSuccess: () => {
    toast.success("Interests saved successfully!", {
      description: "Your profile has been updated",
    });
  },

  interestsError: (message?: string) => {
    toast.error("Failed to save interests", {
      description:
        message || "Don't worry, you can update them later in your profile",
    });
  },

  interestsSkipped: () => {
    toast.info("Interests skipped", {
      description: "You can add them later in your profile settings",
    });
  },
};

/**
 * Form-related toasts
 */
export const formToasts = {
  saveSuccess: (itemName = "Changes") => {
    toast.success(`${itemName} saved successfully`);
  },

  saveError: (message?: string) => {
    toast.error("Failed to save changes", {
      description: message || "Please try again",
    });
  },

  validationError: () => {
    toast.error("Validation error", {
      description: "Please check the form and fix any errors",
    });
  },

  requiredFields: () => {
    toast.warning("Required fields missing", {
      description: "Please fill in all required fields",
    });
  },
};

/**
 * CRUD operation toasts
 */
export const crudToasts = {
  createSuccess: (itemName: string) => {
    toast.success(`${itemName} created successfully`);
  },

  createError: (itemName: string, message?: string) => {
    toast.error(`Failed to create ${itemName}`, {
      description: message || "Please try again",
    });
  },

  updateSuccess: (itemName: string) => {
    toast.success(`${itemName} updated successfully`);
  },

  updateError: (itemName: string, message?: string) => {
    toast.error(`Failed to update ${itemName}`, {
      description: message || "Please try again",
    });
  },

  deleteSuccess: (itemName: string) => {
    toast.success(`${itemName} deleted successfully`);
  },

  deleteError: (itemName: string, message?: string) => {
    toast.error(`Failed to delete ${itemName}`, {
      description: message || "Please try again",
    });
  },

  deleteConfirmation: (itemName: string) => {
    toast.warning(`Delete ${itemName}?`, {
      description: "This action cannot be undone",
    });
  },
};

/**
 * Network-related toasts
 */
export const networkToasts = {
  offline: () => {
    toast.error("No internet connection", {
      description: "Please check your network and try again",
    });
  },

  online: () => {
    toast.success("Back online", {
      description: "Your connection has been restored",
    });
  },

  serverError: () => {
    toast.error("Server error", {
      description: "Something went wrong. Please try again later",
    });
  },

  timeout: () => {
    toast.error("Request timeout", {
      description: "The request took too long. Please try again",
    });
  },
};

/**
 * File upload toasts
 */
export const uploadToasts = {
  uploadStart: (fileName: string) => {
    return toast.loading(`Uploading ${fileName}...`);
  },

  uploadSuccess: (fileName: string) => {
    toast.success(`${fileName} uploaded successfully`);
  },

  uploadError: (fileName: string, message?: string) => {
    toast.error(`Failed to upload ${fileName}`, {
      description: message || "Please try again",
    });
  },

  fileTooLarge: (maxSize: string) => {
    toast.error("File too large", {
      description: `Maximum file size is ${maxSize}`,
    });
  },

  invalidFileType: (allowedTypes: string) => {
    toast.error("Invalid file type", {
      description: `Allowed types: ${allowedTypes}`,
    });
  },
};

/**
 * Shopping/E-commerce toasts
 */
export const shopToasts = {
  addedToCart: (productName: string) => {
    toast.success("Added to cart", {
      description: productName,
    });
  },

  removedFromCart: (productName: string) => {
    toast.success("Removed from cart", {
      description: productName,
    });
  },

  orderPlaced: (orderNumber: string) => {
    toast.success("Order placed successfully!", {
      description: `Order #${orderNumber}`,
    });
  },

  orderError: (message?: string) => {
    toast.error("Failed to place order", {
      description: message || "Please try again",
    });
  },

  paymentSuccess: () => {
    toast.success("Payment successful", {
      description: "Thank you for your purchase!",
    });
  },

  paymentError: (message?: string) => {
    toast.error("Payment failed", {
      description: message || "Please check your payment details",
    });
  },
};

/**
 * Follow/Following related toasts
 */
export const followToasts = {
  followSuccess: (storeName?: string) => {
    toast.success("Following store", {
      description: storeName
        ? `You're now following ${storeName}`
        : "You will now see updates from this store",
    });
  },

  followError: (message?: string) => {
    toast.error("Failed to follow store", {
      description: message || "Please try again",
    });
  },

  unfollowSuccess: (storeName?: string) => {
    toast.success("Store unfollowed", {
      description: storeName
        ? `You unfollowed ${storeName}`
        : "You will no longer see updates from this store",
    });
  },

  unfollowError: (message?: string) => {
    toast.error("Failed to unfollow store", {
      description: message || "Please try again",
    });
  },

  alreadyFollowing: (storeName?: string) => {
    toast.info("Already following", {
      description: storeName
        ? `You're already following ${storeName}`
        : "You're already following this store",
    });
  },
};
